import { useTranslate } from '@im/react-localization';
import {
  Button,
  ButtonProps as LibButtonProps,
} from '@im/ui-lib/zenith/Button';
import { DataAttributes, ToastIntent } from '@im/ui-lib/zenith/common';
import { Icon } from '@im/ui-lib/zenith/Icon';
import { LoadingIndicator } from '@im/ui-lib/zenith/LoadingIndicator';
import { Tooltip, TooltipProps } from '@im/ui-lib/zenith/Tooltip';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { getWidthOfButton } from '../AddToCartButtonV2/commonFunction';
import { ObjectMap } from 'mf_core/query';
import { AddToQuoteToast } from '../AddToQuoteToast';
import { ButtonType } from './types/Button.type';
import { useAddToQuote } from './hooks';
import { truncateText } from '../utils/truncateText.util';

type AddToQuoteMessage = {
  show: boolean;
  toastIntent?: ToastIntent;
};

interface TSAddToQuoteButtonProps {
  buttonLoadingProps?: ButtonProps;
  buttonProps?: LibButtonProps;
  buttonStyles?: ObjectMap;
  buttonType: ButtonType;
  dataAttributes?: DataAttributes;
  enableToast?: boolean;
  engineId?: string;
  handleAddToQuote?: (listOfProducts: any[]) => void;
  linkRefNumber?: string;
  productDetails?: ObjectMap;
  quantity: number;
  recommendationID?: string;
  selectedWarrantyData?: any;
  tooltipProps?: Omit<TooltipProps, 'children'>;
}

interface ButtonProps {
  isAdding?: boolean;
  isSuccessful?: boolean;
}

const TSAddToQuoteButton = (props: TSAddToQuoteButtonProps) => {
  const {
    buttonLoadingProps,
    buttonProps: buttonAddlProps,
    buttonStyles,
    buttonType,
    dataAttributes,
    enableToast,
    engineId,
    handleAddToQuote: handleAddToQuoteProps,
    linkRefNumber,
    productDetails,
    quantity,
    recommendationID,
    selectedWarrantyData,
  } = props;

  const { addToQuote, quoteEditorURL, quoteInfo } = useAddToQuote();
  const theme = useTheme();
  const translate = useTranslate();

  const [buttonProps, setButtonProps] = useState<ButtonProps>({
    isAdding: false,
    isSuccessful: false,
  });
  const [addToQuoteMessage, setAddToQuoteMessage] = useState<AddToQuoteMessage>(
    { show: false }
  );

  useEffect(() => {
    if (buttonProps.isSuccessful) {
      const timer = setTimeout(() => {
        setButtonProps({ isAdding: false, isSuccessful: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [buttonProps.isSuccessful]);

  useEffect(() => {
    if (buttonLoadingProps?.isAdding) {
      setButtonProps({ isAdding: true, isSuccessful: false });
    } else if (buttonLoadingProps?.isSuccessful) {
      setButtonProps({ isAdding: false, isSuccessful: false });
    }
  }, [buttonLoadingProps]);

  const handleAddToQuote = async () => {
    try {
      const products: any[] = [
        {
          engineId: engineId,
          isNonTransactional: productDetails?.isNonTransactional,
          linkWarrantyRefNumber: linkRefNumber,
          quantity,
          recommendationRecordId: recommendationID,
          skuVpnNumber: productDetails?.partNumber || productDetails?.sku,
          vendorName: productDetails?.vendorname,
          vendorcode: productDetails?.vendorcode,
        },
      ];
      if (selectedWarrantyData?.warranty?.erpWarrantyEnabled) {
        products[0].hasWarrantyProduct = quoteInfo.enableWarranty;
        products.push({
          engineId: selectedWarrantyData?.warranty?.engineId,
          isNonTransactional:
            selectedWarrantyData?.warranty?.isNonTransactional,
          isWarrantyProduct: quoteInfo.enableWarranty,
          parentProduct: quoteInfo.enableWarranty
            ? products[0].skuVpnNumber
            : '',
          quantity,
          recommendationRecordId: recommendationID,
          skuVpnNumber: selectedWarrantyData?.warranty?.partNumber,
          vendorName: selectedWarrantyData?.warranty?.vendorname,
          vendorcode: selectedWarrantyData?.warranty?.vendorcode,
        });
      }

      if (handleAddToQuoteProps && quoteInfo.enableWarranty) {
        handleAddToQuoteProps(products);
      } else {
        setButtonProps({ isAdding: true, isSuccessful: false });
        await addToQuote('product', products);
        setButtonProps({ isAdding: false, isSuccessful: true });
        if (enableToast) {
          setAddToQuoteMessage({ show: true, toastIntent: 'success' });
        }
      }
    } catch (e) {
      setButtonProps({ isAdding: false, isSuccessful: false });
      setAddToQuoteMessage({ show: true, toastIntent: 'error' });
    }
  };

  const onHideToast = () => setAddToQuoteMessage({ show: false });

  const getButtonText = useCallback(() => {
    if (buttonProps.isSuccessful) {
      if (buttonType === ButtonType.iconwithcheckmark) {
        return <Icon iconId="Check" />;
      } else {
        return translate('Common.Added');
      }
    } else if (buttonProps.isAdding) {
      const showText = [ButtonType.textwithicon, ButtonType.text].includes(
        buttonType
      );
      return (
        <Box alignItems="center" display="flex" gap={'8px'}>
          <LoadingIndicator
            color="#fff"
            loadingStyle="line-simple"
            spinnerSize={20}
            value={40}
            variant="indeterminate"
          />
          {showText ? translate('Common.Adding') : null}
        </Box>
      );
    } else if (
      [ButtonType.icon, ButtonType.iconwithcheckmark].includes(buttonType)
    ) {
      return <Icon iconId="QuotePlus" />;
    } else {
      return translate('ProductConfig.AddToQuote');
    }
  }, [buttonProps.isAdding, buttonProps.isSuccessful, buttonType, translate]);

  const description: string =
    productDetails?.additionalAttributes?.[0]?.longDescription ||
    productDetails?.longDescription;

  return (
    <>
      <Tooltip label={translate('ProductConfig.AddToQuote')}>
        <span
          style={{ width: getWidthOfButton(buttonStyles, buttonAddlProps) }}
        >
          <Button
            {...dataAttributes}
            {...buttonAddlProps}
            disabled={Boolean(
              buttonProps.isAdding || buttonAddlProps?.disabled
            )}
            fullWidth
            onClick={handleAddToQuote}
            size="medium"
            startIcon={
              buttonType === ButtonType.textwithicon ? (
                <Icon iconId="QuotePlus" />
              ) : null
            }
            sx={{
              ...buttonStyles,
              ...(buttonProps.isSuccessful
                ? {
                    backgroundColor: `${theme.palette.success[700]}!important`,
                  }
                : undefined),
            }}
            variant="primary"
          >
            {getButtonText()}
          </Button>
        </span>
      </Tooltip>
      {addToQuoteMessage.show && description && (
        <AddToQuoteToast
          {...addToQuoteMessage}
          description={`${quantity} x ${truncateText(description)}`}
          onHideToast={onHideToast}
          quoteEditorURL={quoteEditorURL}
        />
      )}
    </>
  );
};

export default TSAddToQuoteButton;
